import React from 'react';

import { dashboardPaymentSlipLogApis } from '@apis/next/paymentSlipLogApis';
import { Layout } from '@components/Layout';
import { PrintableContainer } from '@components/Printable';
import { cellFactory, Table } from '@components/Table/Table';
import { getChangeLogDefaultDetail } from '@data/changeLog';
import { moneyStrings } from '@data/money';
import { paymentDetailsStrings, paymentSlipStrings } from '@data/paymentSlip';
import { useSearch } from '@hooks/next/useSearch';
import { PaymentDetails, PaymentDetailsHelper } from '@models/next/PaymentSlip';
import { PaymentSlipLogFormatters } from '@models/next/PaymentSlipLog';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { formatDateTime, mapOptional, toJST } from '@utils/types';
import clsx from 'clsx';
import { max } from 'lodash-es';
import { equals } from 'remeda';
import { z } from 'zod';

const useStyles = makeMuiStyles((theme, _common) => ({
  customInner: {
    maxWidth: '65rem',
  },
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  dataNotExist: {
    color: theme.palette.grey[400],
    margin: '3rem auto',
  },
}));

const strings = createStrings({
  ja: {
    title: `${paymentSlipStrings.payment_slip} - ${paymentSlipStrings.change_log}`,
    dataNotExist: '履歴なし',
    no: 'No',
    updatedAt: '更新時間',
    editor: '編集者',
    editedField: '変更内容',
    beforeValue: '変更前',
    afterValue: '変更後',
    detail: '変更理由',
    unitNumType: '数量・単位',
  },
});

const PaymentSlipDetailsDisplay: React.FC<{ item: PaymentDetails }> = ({ item }) => {
  const helper = new PaymentDetailsHelper(item);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '0.5rem' }}>
      <div>{paymentDetailsStrings.name}</div>
      <div>{item.name}</div>

      <div>{paymentDetailsStrings.memo}</div>
      <div style={{ maxWidth: 150 }}>{item.memo}</div>

      <div>{paymentDetailsStrings.unit_price}</div>
      <div>{moneyStrings.formatYenFront(item.unit_price)}</div>

      <div>{strings.unitNumType}</div>
      <div>
        {item.unit_num} {item.unit_num_type}
      </div>

      <div>{paymentDetailsStrings.non_tax_amount}</div>
      <div>{moneyStrings.formatYenFront(helper.getTaxExcludedTotalPrice())}</div>

      <div>{paymentDetailsStrings.excise_tax}</div>
      <div>{moneyStrings.formatYenFront(item.excise_tax)}</div>
    </div>
  );
};

export const PrintPaymentSlipLog: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    search: { id },
  } = useSearch(
    z.object({
      id: z.number(),
    }),
  );

  const paymentSlipLogQuery = dashboardPaymentSlipLogApis.useGet(
    '/dashboard/payment-slip-log/:id',
    {
      params: {
        id,
      },
    },
  );

  let cellIndex = 0;

  const formatters: PaymentSlipLogFormatters = {
    invoice_file: (v) => v?.name ?? '-',
  };

  const bodyColumns =
    paymentSlipLogQuery.data?.flatMap((item) => {
      if (item.edited_field === 'payment_details') {
        const edited = item.before_value.filter((v) =>
          item.after_value.some((after) => after.id === v.id && !equals(after, v)),
        );

        const deleted = item.before_value.filter(
          (v) => !item.after_value.some((after) => after.id === v.id),
        );

        const added = item.after_value.filter(
          (v) => !item.before_value.some((before) => before.id === v.id),
        );

        return [
          ...edited.map((paymentSlipDetails) => [
            cellFactory.create((++cellIndex).toString()),
            cellFactory.create(item.editor || '-'),
            cellFactory.create(formatDateTime(item.updated_at)),
            cellFactory.create(item.label + '・編集'),
            cellFactory.create(<PaymentSlipDetailsDisplay item={paymentSlipDetails} />),
            cellFactory.create(
              mapOptional(
                item.after_value.find((item) => item.id === paymentSlipDetails.id),
                (item) => <PaymentSlipDetailsDisplay item={item} />,
              ),
            ),
            cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
          ]),
          ...added.map((paymentSlipDetails) => [
            cellFactory.create((++cellIndex).toString()),
            cellFactory.create(item.editor || '-'),
            cellFactory.create(formatDateTime(item.updated_at)),
            cellFactory.create(item.label + '・追加'),
            cellFactory.create('-'),
            cellFactory.create(<PaymentSlipDetailsDisplay item={paymentSlipDetails} />),
            cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
          ]),
          ...deleted.map((paymentSlipDetails) => [
            cellFactory.create((++cellIndex).toString()),
            cellFactory.create(item.editor || '-'),
            cellFactory.create(formatDateTime(item.updated_at)),
            cellFactory.create(item.label + '・削除'),
            cellFactory.create(<PaymentSlipDetailsDisplay item={paymentSlipDetails} />),
            cellFactory.create('-'),
            cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
          ]),
        ];
      } else {
        // Bug in typescript
        const formatter = (formatters[item.edited_field] ?? String) as typeof String;

        return [
          [
            cellFactory.create((++cellIndex).toString()),
            cellFactory.create(item.editor || '-'),
            cellFactory.create(formatDateTime(item.updated_at)),
            cellFactory.create(item.label || '-'),
            cellFactory.create(formatter(item.before_value) || '-'),
            cellFactory.create(formatter(item.after_value) || '-'),
            cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
          ],
        ];
      }
    }) ?? [];

  let contents: React.ReactNode = null;
  const head = (
    <Table.Head>
      <Table.Row
        columns={[
          cellFactory.create(strings.no || '-'),
          cellFactory.create(strings.editor || '-'),
          cellFactory.create(strings.updatedAt || '-'),
          cellFactory.create(strings.editedField || '-'),
          cellFactory.create(strings.beforeValue || '-'),
          cellFactory.create(strings.afterValue || '-'),
          cellFactory.create(strings.detail || '-'),
        ]}
      />
    </Table.Head>
  );

  if (paymentSlipLogQuery.data && paymentSlipLogQuery.data?.length > 0) {
    contents = (
      <Table>
        {head}
        <Table.Body>
          {bodyColumns.map((columns) => {
            return <Table.Row columns={columns} />;
          })}
        </Table.Body>
      </Table>
    );
  } else if (paymentSlipLogQuery.data && paymentSlipLogQuery.data?.length === 0) {
    contents = (
      <>
        <Table>{head}</Table>
        <p className={classes.dataNotExist}>{strings.dataNotExist}</p>
      </>
    );
  }

  const updatedAt =
    paymentSlipLogQuery.data && max(paymentSlipLogQuery.data.map((d) => d.updated_at));

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      loading={paymentSlipLogQuery.isLoading}
      title={strings.title}
      errors={[paymentSlipLogQuery.error]}
      classes={{
        background: classes.background,
      }}
    >
      <PrintableContainer classes={{ inner: classes.customInner }}>
        {updatedAt && (
          <p className={clsx(classes.horizontalSpace1, classes.middleText)}>
            <span>Latest Update:</span>
            <span>{updatedAt ? toJST(new Date(updatedAt)) : '-'}</span>
          </p>
        )}
        {contents}
      </PrintableContainer>
    </Layout>
  );
};
